import { AppDialogHelper } from 'general/components/AppDialog';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KTTooltip from '../../KTTooltip';
import ToastHelper from 'general/helpers/ToastHelper';

KTVideoInput.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    acceptVideoTypes: PropTypes.arrayOf(PropTypes.string),
    defaultVideo: PropTypes.string,
    editable: PropTypes.bool,
    disabled: PropTypes.bool,
    onSelectedFile: PropTypes.func,
    onRemovedFile: PropTypes.func,
    onForceRemoveFile: PropTypes.func,
};

KTVideoInput.defaultProps = {
    value: '',
    acceptVideoTypes: ['video/mp4', 'video/avi', 'video/mov'],
    defaultVideo: '',
    editable: false,
    disabled: false,
    onSelectedFile: null,
    onRemovedFile: null,
    onForceRemoveFile: null,
};

/**
 * @param {{
 * name: string,
 * value: string,
 * acceptVideoTypes: string[],
 * defaultVideo: string,
 * editable: boolean,
 * disabled: boolean,
 * onSelectedFile: function,
 * onRemovedFile: function,
 * onForceRemoveFile: function,
 * }} props
 * @returns
 */
function KTVideoInput(props) {
    const {
        name,
        value,
        acceptVideoTypes,
        defaultVideo,
        editable,
        disabled,
        onSelectedFile,
        onRemovedFile,
        onForceRemoveFile,
    } = props;

    const [inputValue, setInputValue] = useState(value);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    function handleChange(e) {
        if (!_.isEmpty(inputValue)) {
            URL.revokeObjectURL(inputValue);
        }
        const videos = e.target.files;
        if (videos && videos.length > 0) {
            const selectedVideo = videos[0];
            const MAX_VIDEO_SIZE_MB = 100;
            const sizeInMB = selectedVideo.size / (1024 * 1024);

            console.log('sizeInMB: ', sizeInMB);

            if (sizeInMB > MAX_VIDEO_SIZE_MB) {
                ToastHelper.showError(`Video vượt quá dung lượng tối đa ${MAX_VIDEO_SIZE_MB}MB.`);
            } else {
                const videoInputValue = URL.createObjectURL(selectedVideo);
                setInputValue(videoInputValue);
                if (onSelectedFile) onSelectedFile(selectedVideo);
            }
        }
        e.target.value = null;
    }

    function handleRemoveVideo() {
        if (onForceRemoveFile) {
            onForceRemoveFile();
            return;
        }
        if (!_.isEmpty(inputValue)) {
            URL.revokeObjectURL(inputValue);
        }
        setInputValue('');
        if (onRemovedFile) onRemovedFile();
        // Reset file input to ensure it can handle new file selection
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    }

    const handleDragIn = (e) => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragOut = (e) => {
        e.preventDefault();
        setDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file && acceptVideoTypes.includes(file.type)) {
            const MAX_VIDEO_SIZE_MB = 100;
            const sizeInMB = file.size / (1024 * 1024);

            if (sizeInMB > MAX_VIDEO_SIZE_MB) {
                ToastHelper.showError(`Video vượt quá dung lượng tối đa ${MAX_VIDEO_SIZE_MB}MB.`);
            } else {
                const videoInputValue = URL.createObjectURL(file);
                setInputValue(videoInputValue);
                if (onSelectedFile) onSelectedFile(file);
            }
        }
    };

    return (
        <div className="video-input-container">
            {_.isEmpty(inputValue) || _.isNull(inputValue) ? (
                <button
                    className="upload-button border-none px-6 py-4 rounded-sm"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={handleDragIn}
                    onDragLeave={handleDragOut}
                    onDrop={handleDrop}
                    style={{ backgroundColor: AppColors.gray[200] }}
                >
                    <i className="fa-regular fa-upload mr-3" style={{ color: AppColors.gray[800] }} />
                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                        Thêm tệp tin
                    </span>
                </button>
            ) : (
                <div className="video-preview">
                    <video
                        src={inputValue || defaultVideo}
                        controls
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    {!disabled && editable && (
                        <KTTooltip text={'Xóa'}>
                            <span
                                className={`btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow`}
                                data-action="remove"
                                onClick={handleRemoveVideo}
                                style={{ position: 'absolute', bottom: '-5px', right: '-5px' }}
                            >
                                <i className="ki ki-bold-close icon-xs text-muted" />
                            </span>
                        </KTTooltip>
                    )}
                </div>
            )}
            <input
                type="file"
                name={name}
                id={name}
                ref={fileInputRef}
                accept={_.join(acceptVideoTypes, ', ')}
                onChange={handleChange}
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default KTVideoInput;